import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, TextField, MenuItem, Chip, IconButton,
  Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Snackbar, Grid, Tooltip,
} from "@mui/material";
import AddIcon    from "@mui/icons-material/Add";
import EditIcon   from "@mui/icons-material/Edit";
import WarningIcon from "@mui/icons-material/Warning";
import api from "../../utils/api";

const SEVERITY_COLOR = { minor:"#2f63ad", moderate:"#b45309", major:"#dc2626", total_loss:"#7c3aed" };
const TYPE_LABELS    = { damage:"Damage",incident:"Incident",theft:"Theft",grounding:"Grounding",other:"Other" };
const cardSx  = { borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", boxShadow:"0 4px 16px rgba(0,0,0,0.05)", p:2.5, mb:2.5 };
const fieldSx = { "& .MuiOutlinedInput-root":{ borderRadius:"10px" } };
const btnSx   = { backgroundColor:"#2f63ad", color:"white", fontWeight:700, borderRadius:"10px", textTransform:"none", boxShadow:"none", "&:hover":{ backgroundColor:"#1f4f94", boxShadow:"none" } };

const MarinaIncidents = ({ marinaId }) => {
  const [incidents, setIncidents] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [open,      setOpen]      = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [form,      setForm]      = useState({ incident_type:"damage", severity:"minor", description:"", repair_cost:"", slip_id:"", reservation_id:"", vessel_id:"" });
  const [snack,     setSnack]     = useState({ open:false, msg:"", sev:"success" });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/marina-incidents?marina_id=${marinaId}`); setIncidents(r.data||[]); }
    catch { setSnack({ open:true, msg:"Failed to load incidents.", sev:"error" }); }
    finally { setLoading(false); }
  }, [marinaId]);

  useEffect(() => { if (marinaId) load(); }, [marinaId, load]);

  const handleSave = async () => {
    try {
      if (editing) {
        await api.patch(`/marina-incidents/${editing}`, { status: form.status, repair_cost: form.repair_cost, notes: form.notes });
        setSnack({ open:true, msg:"Incident updated.", sev:"success" });
      } else {
        await api.post("/marina-incidents", { ...form, marina_id: marinaId });
        setSnack({ open:true, msg:"Incident reported.", sev:"success" });
      }
      setOpen(false); setEditing(null);
      setForm({ incident_type:"damage", severity:"minor", description:"", repair_cost:"", slip_id:"", reservation_id:"", vessel_id:"" });
      load();
    } catch { setSnack({ open:true, msg:"Failed to save.", sev:"error" }); }
  };

  const fmtDT = d => d ? new Date(d).toLocaleDateString() : "N/A";

  return (
    <Box>
      <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", mb:3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight:700, color:"#223142" }}>Incidents & Damage Reports</Typography>
          <Typography variant="body2" sx={{ color:"#6b778c" }}>Log damage, incidents, and maintenance issues for slips and vessels.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={()=>{ setOpen(true); setEditing(null); }} sx={btnSx}>
          Report Incident
        </Button>
      </Box>

      {loading ? <CircularProgress /> : (
        <Paper elevation={0} sx={{ borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
          <Table size="small">
            <TableHead sx={{ bgcolor:"rgba(47,99,173,0.04)" }}>
              <TableRow>{["Type","Severity","Slip","Description","Reported","Repair Cost","Status",""].map(h=><TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>)}</TableRow>
            </TableHead>
            <TableBody>
              {incidents.map(i=>(
                <TableRow key={i.id} hover>
                  <TableCell><Chip size="small" label={TYPE_LABELS[i.incident_type]||i.incident_type} sx={{ fontWeight:700, fontSize:11, borderRadius:"8px" }} /></TableCell>
                  <TableCell><Chip size="small" label={i.severity} sx={{ fontWeight:700, fontSize:11, borderRadius:"8px", bgcolor:`${SEVERITY_COLOR[i.severity]||"#2f63ad"}15`, color:SEVERITY_COLOR[i.severity]||"#2f63ad" }} /></TableCell>
                  <TableCell sx={{ fontSize:12 }}>{i.slip_number||"N/A"}</TableCell>
                  <TableCell sx={{ fontSize:12, maxWidth:200 }}><Tooltip title={i.description||""}><Typography variant="caption" noWrap sx={{ display:"block", maxWidth:180 }}>{i.description||"N/A"}</Typography></Tooltip></TableCell>
                  <TableCell sx={{ fontSize:12 }}>{fmtDT(i.reported_at)}</TableCell>
                  <TableCell sx={{ fontSize:12 }}>{i.repair_cost ? `$${Number(i.repair_cost).toFixed(2)}` : "N/A"}</TableCell>
                  <TableCell><Chip size="small" label={i.status} sx={{ fontWeight:700, fontSize:11, borderRadius:"8px", bgcolor: i.status==="open"?"rgba(220,38,38,0.1)":"rgba(34,197,94,0.1)", color: i.status==="open"?"#dc2626":"#16a34a" }} /></TableCell>
                  <TableCell><IconButton size="small" onClick={()=>{ setEditing(i.id); setForm({...i}); setOpen(true); }}><EditIcon fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
              {!incidents.length && <TableRow><TableCell colSpan={8} sx={{ textAlign:"center", py:4, color:"#6b778c" }}>No incidents reported.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={open} onClose={()=>setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx:{ borderRadius:"16px" } }}>
        <DialogTitle sx={{ fontWeight:700, color:"#223142", display:"flex", alignItems:"center", gap:1 }}>
          <WarningIcon sx={{ color:"#dc2626" }} />{editing ? "Update Incident" : "Report Incident"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt:0.5 }}>
            {!editing && (<>
              <Grid item xs={6}><TextField select fullWidth size="small" label="Type" value={form.incident_type} onChange={e=>setForm(f=>({...f,incident_type:e.target.value}))} sx={fieldSx}>{Object.entries(TYPE_LABELS).map(([v,l])=><MenuItem key={v} value={v}>{l}</MenuItem>)}</TextField></Grid>
              <Grid item xs={6}><TextField select fullWidth size="small" label="Severity" value={form.severity} onChange={e=>setForm(f=>({...f,severity:e.target.value}))} sx={fieldSx}>{["minor","moderate","major","total_loss"].map(s=><MenuItem key={s} value={s}>{s.replace("_"," ")}</MenuItem>)}</TextField></Grid>
              <Grid item xs={6}><TextField fullWidth size="small" label="Slip ID (optional)" value={form.slip_id} onChange={e=>setForm(f=>({...f,slip_id:e.target.value}))} sx={fieldSx} /></Grid>
              <Grid item xs={6}><TextField fullWidth size="small" label="Vessel ID (optional)" value={form.vessel_id} onChange={e=>setForm(f=>({...f,vessel_id:e.target.value}))} sx={fieldSx} /></Grid>
              <Grid item xs={12}><TextField fullWidth multiline rows={3} size="small" label="Description" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} sx={fieldSx} /></Grid>
            </>)}
            <Grid item xs={6}><TextField fullWidth size="small" label="Repair Cost ($)" type="number" value={form.repair_cost} onChange={e=>setForm(f=>({...f,repair_cost:e.target.value}))} sx={fieldSx} /></Grid>
            {editing && (
              <Grid item xs={6}><TextField select fullWidth size="small" label="Status" value={form.status||"open"} onChange={e=>setForm(f=>({...f,status:e.target.value}))} sx={fieldSx}>{["open","investigating","resolved"].map(s=><MenuItem key={s} value={s}>{s}</MenuItem>)}</TextField></Grid>
            )}
            {editing && <Grid item xs={12}><TextField fullWidth multiline rows={2} size="small" label="Resolution Notes" value={form.notes||""} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} sx={fieldSx} /></Grid>}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px:3, pb:3, gap:1 }}>
          <Button onClick={()=>setOpen(false)} variant="outlined" sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600 }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" sx={btnSx}>{editing ? "Update" : "Submit Report"}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top", horizontal:"center" }}>
        <Alert severity={snack.sev}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default MarinaIncidents;
